import { DatadogLogger } from "../../logging/DatadogLogger";
import { TareCodegenTst } from "../../proctor/__generated__/SharedProctorSpecifications";
import { applyProctorOverride } from "../../proctor/applyProctorOverride";
import { getProctorOverrides } from "../../proctor/proctorOverrideStore";
import { getProctorAllocation } from "./getProctorAllocation";

/**
 * Proctor test gating the TARE codegen migration: moving TARE reads off the
 * legacy `RNTareBridge` native module onto the codegen'd Turbo modules (e.g.
 * `NativeJSTAccountStatus`). Users in the `Active` bucket use codegen.
 *
 * Re-exported from the generated spec so existing imports keep resolving here.
 */
export { TareCodegenTst };

// The proctor bridge only exposes an async allocation API, so cache the most
// recent result for synchronous callers (e.g. getAccountStatusSync), defaulting
// to the legacy bridge until the async allocation resolves at least once.
let cachedIsEnabled = false;

// The resolved value can flip within a session (e.g. the server allocation lands
// after `getForcedWebBucketsString` overrides seed, or the async allocation
// resolves late). Once `isTareCodegenActive` has returned false at least once,
// the first subsequent true is treated as drift and logged a single time.
let hasReturnedFalse = false;
let hasLoggedDrift = false;

/**
 * Record each resolved activation value and emit a one-shot Datadog warning the
 * first time the flag returns true after having previously returned false.
 */
function logCodegenActivationDrift(isActive: boolean): void {
  if (!isActive) {
    hasReturnedFalse = true;
    return;
  }

  if (!hasReturnedFalse || hasLoggedDrift) {
    return;
  }
  // Mark optimistically so concurrent calls don't double-log, then reset if the
  // telemetry rejects so a later check can retry the one-shot warning.
  hasLoggedDrift = true;

  DatadogLogger.warn("tare.codegen.allocation_drift", {
    test: TareCodegenTst.name,
    driftDirection: "falseThenTrue",
  }).catch(() => {
    hasLoggedDrift = false;
  });
}

/**
 * Resolve whether the TARE codegen migration is active for the current viewer,
 * refreshing the synchronous cache as a side effect.
 */
export async function isTareCodegenActive(): Promise<boolean> {
  try {
    const allocation = await getProctorAllocation(TareCodegenTst);
    // Honor locally-forced buckets (debug web groups / prforceGroups) the same
    // way the generated `…ProctorProvider` does for React consumers.
    const baseAllocations = allocation ? { [TareCodegenTst.name]: allocation } : {};
    const merged = applyProctorOverride([TareCodegenTst], baseAllocations, getProctorOverrides());
    const value = merged[TareCodegenTst.name]?.value ?? TareCodegenTst.buckets.Inactive;
    cachedIsEnabled = value >= TareCodegenTst.buckets.Active;
  } catch {
    // Fail closed to the legacy bridge if the lookup rejects; callers never see
    // a rejection.
    cachedIsEnabled = false;
  }
  logCodegenActivationDrift(cachedIsEnabled);
  return cachedIsEnabled;
}

/**
 * Best-effort synchronous read of the codegen migration flag: returns the last
 * cached value (defaulting to `false`/legacy bridge) and kicks off a refresh.
 */
export function isTareCodegenActiveSync(): boolean {
  void isTareCodegenActive();
  return cachedIsEnabled;
}
